/**
 * Use the promotional rail to feature 5+ promoted stories that are related. For example, Beauty steals during a major event. Use when a higher prominence and/or creative image is needed.
 *
 * @module views/components/PromotionalRailGrid
 * @memberof -Common
 */
import './PromotionalRailGrid.scss';

import React from 'react';

import classNames from 'classnames';
import PropTypes from 'prop-types';

import AsyncComponent from '@ulta/core/components/AsyncComponent/AsyncComponent';
import GridContainer from '@ulta/core/components/GridContainer/GridContainer';
import Rail, { Slide } from '@ulta/core/components/Rail/Rail';
import { constants } from '@ulta/core/utils/constants/constants';
import { spreadProps } from '@ulta/core/utils/dom/dom';

import PromotionalGrid from '@ulta/components/PromotionalGrid/PromotionalGrid';

/**
 * Represents a PromotionalRailGrid component
 *
 * @method
 * @param {PromotionalRailGridProps} props - React properties passed from composition
 * @returns PromotionalRailGrid
 */
export const PromotionalRailGrid = function( props ){
  const {
    title,
    subtitle,
    backgroundColor,
    items,
    _railADAmeta,
    _railMeta,
    isGrid,
    previousClickAction,
    nextClickAction,
    spaceBetween,
    viewAllAction,
    salePriceAccessibilityLabel,
    listPriceAccessibilityLabel,
    alignment = defaultProps.alignment,
    sliderConfig = defaultProps.sliderConfig
  } = props;

  if( !items ){
    return null;
  }
  return (
    <div
      className={ classNames( 'PromotionalRailGrid', {
        PromotionalRailGrid__withBackgroundColor: backgroundColor
      } ) }
    >
      { isGrid && (
        <PromotionalGrid
          backgroundColor={ backgroundColor }
          title={ title }
          subtitle={ subtitle }
          items={ items }
          alignment={ alignment }
        />
      ) }
      { !isGrid && (
        <GridContainer
          { ...spreadProps( {
            fullBleedContentBackgroundColor: backgroundColor
          } ) }
        >
          <Rail
            items={ items }
            viewAllAction={ viewAllAction }
            previousClickAction={ previousClickAction }
            nextClickAction={ nextClickAction }
            spaceBetween={ spaceBetween }
            _railADAmeta={ _railADAmeta }
            _railMeta={ _railMeta }
            { ...spreadProps( sliderConfig ) }
          >
            { items?.length > 0 &&
              items?.map( ( module, moduleIndex ) => (
                <Slide key={ `Rail${moduleIndex + 1} ` }>
                  <AsyncComponent
                    moduleName='PromotionalCard'
                    { ...spreadProps( { ...module } ) }
                    salePriceAccessibilityLabel={ salePriceAccessibilityLabel }
                    listPriceAccessibilityLabel={ listPriceAccessibilityLabel }
                  />
                </Slide>
              ) ) }
          </Rail>
        </GridContainer>
      ) }
    </div>
  );
};

/**
 * Default values for passed properties
 * @type {PromotionalRailGridProps}
 * @property {object} INTERSECTION_OBSERVER_OPTIONS - The default initial values for intersection observer.
 */
export const defaultProps = {
  ...constants.INTERSECTION_OBSERVER_OPTIONS,
  alignment: 'bottom',
  sliderConfig: {
    sliderPerViewXL: 4,
    slidesPerGroupXL: 4,
    sliderPerViewLG: 3,
    slidesPerGroupLG: 3,
    sliderPerViewMD: 3,
    slidesPerGroupMD: 3,
    sliderPerViewSM: 1,
    slidesPerGroupSM: 1
  }
};
/**
 * Property type definitions
 *
 * @typedef PromotionalRailGridProps
 * @type {object}
 * @property {string} title - Sets the headline title.
 * @property {string} subtitle - Sets the subtitle.
 * @property {object} viewAllAction - sets the url for action label.
 * @property {array} items - Set the Item array.
 * @property {string} backgroundColor - Sets the backgroundColor
 * @property {string} alignment - sets the alignment for the text.
 * @property {boolean} isGrid - sets the value true/false based on value
 * @property {object} previousClickAction - The prop that holds the action for the previous button
 * @property {object} nextClickAction - The prop that holds the action for the next button.
 * @property {object} _railADAmeta - sets carouselAccessibility
 * @property {object} _railMeta - sets nextAccessibility, previousAccessibility
 * @property {string} listPriceAccessibilityLabel - Sets the list price accessibility label for screen readers
 * @property {string} salePriceAccessibilityLabel - Sets the sale price accessibility label for screen readers
 */

export const propTypes = {
  title: PropTypes.oneOfType( [PropTypes.string, PropTypes.object] ),
  subtitle: PropTypes.string,
  viewAllAction: PropTypes.object,
  items: PropTypes.array,
  backgroundColor: PropTypes.string,
  alignment: PropTypes.string,
  isGrid: PropTypes.bool,
  _railADAmeta: PropTypes.object,
  _railMeta: PropTypes.object,
  previousClickAction: PropTypes.object,
  nextClickAction: PropTypes.object,
  spaceBetween: PropTypes.number,
  sliderConfig: PropTypes.object,
  listPriceAccessibilityLabel: PropTypes.string,
  salePriceAccessibilityLabel: PropTypes.string
};

PromotionalRailGrid.propTypes = propTypes;
PromotionalRailGrid.defaultProps = defaultProps;

export default PromotionalRailGrid;
